import { useCallback, useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { toast } from 'sonner';
import { getSocket } from '@/lib/socket';
import { type RoomAccess } from '@/lib/api';
import type { DeviceBreakdown, Room } from '@/lib/types';

interface UseRoomState {
  room: Room | null;
  currentIndex: number;
  loading: boolean;
}

/**
 * State + actions for the room the operator has joined/created.
 * Owns socket listeners so unmount cleanly removes them.
 */
export function useRoom() {
  const [state, setState] = useState<UseRoomState>({ room: null, currentIndex: 0, loading: false });
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    let disposed = false;
    getSocket().then((s) => {
      if (disposed) return;
      setSocket(s);
    });
    return () => {
      disposed = true;
    };
  }, []);

  useEffect(() => {
    if (!socket) return;

    const onJoin = (roomObj: Room) => {
      setState({ room: roomObj, currentIndex: roomObj.currentIndex ?? 0, loading: false });
    };
    const onUserJoined = (msg: { id: string; userCount: number; deviceBreakdown?: DeviceBreakdown }) => {
      setState((s) =>
        s.room
          ? { ...s, room: { ...s.room, userCount: msg.userCount, deviceBreakdown: msg.deviceBreakdown } }
          : s,
      );
      toast.success(`Client joined`, { description: `${msg.userCount} in room` });
    };
    const onUserLeft = (msg: { id: string; userCount: number; deviceBreakdown?: DeviceBreakdown }) => {
      setState((s) =>
        s.room
          ? { ...s, room: { ...s.room, userCount: msg.userCount, deviceBreakdown: msg.deviceBreakdown } }
          : s,
      );
      toast.message(`Client left`, { description: `${msg.userCount} in room` });
    };
    const onRoomAnchorSet = (pose: Room['anchorPose']) => {
      setState((s) => (s.room ? { ...s, room: { ...s.room, anchorPose: pose } } : s));
    };
    const onRoomAnchorCleared = () => {
      setState((s) => (s.room ? { ...s, room: { ...s.room, anchorPose: null } } : s));
    };
    const onModelChanged = (asset: { model: string } | null) => {
      setState((s) => {
        if (!s.room?.sequence) return s;
        const idx = s.room.sequence.assets.findIndex((a) => a.model === asset?.model);
        return { ...s, currentIndex: idx >= 0 ? idx : s.currentIndex };
      });
    };
    const onRoomClosed = () => {
      setState({ room: null, currentIndex: 0, loading: false });
      toast.message('Room closed');
    };
    const onCurrentModel = (asset: { model: string }) => {
      setState((s) => {
        if (!s.room?.sequence) return s;
        const idx = s.room.sequence.assets.findIndex((a) => a.model === asset.model);
        return { ...s, currentIndex: idx >= 0 ? idx : 0 };
      });
    };
    const onSequenceUpdated = (payload: {
      sequence: { assets: Array<{ model: string; externalURL?: string }> };
      currentIndex: number;
      currentModel: { model: string } | null;
    }) => {
      setState((s) => {
        if (!s.room) return s;
        return {
          ...s,
          room: {
            ...s.room,
            sequence: payload.sequence,
            currentModel: payload.currentModel,
            currentIndex: payload.currentIndex,
          },
          currentIndex: payload.currentIndex,
        };
      });
      toast.message('Sequence updated', {
        description: 'Reloaded live from the server.',
      });
    };

    socket.on('join', onJoin);
    socket.on('userJoined', onUserJoined);
    socket.on('userLeft', onUserLeft);
    socket.on('roomAnchorSet', onRoomAnchorSet);
    socket.on('roomAnchorCleared', onRoomAnchorCleared);
    socket.on('modelChanged', onModelChanged);
    socket.on('currentModel', onCurrentModel);
    socket.on('sequenceUpdated', onSequenceUpdated);
    socket.on('roomClosed', onRoomClosed);
    const onCreateFailed = (payload: { reason: string; name?: string }) => {
      setState((s) => ({ ...s, loading: false }));
      if (payload.reason === 'name_taken') {
        toast.error(`"${payload.name}" is already in use`, {
          description: 'Pick a different room name.',
        });
      } else if (payload.reason === 'invalid_name') {
        toast.error('Room name must be 1–64 characters.');
      } else {
        toast.error('Could not create room', { description: payload.reason });
      }
    };
    const onForbidden = (payload: { reason?: string; visibility?: string; closed?: boolean }) => {
      setState((s) => ({ ...s, loading: false }));
      if (payload.reason === 'unknown') {
        toast.error('That room no longer exists');
      } else if (payload.closed) {
        toast.error('Room is closed');
      } else if (payload.visibility === 'admin') {
        toast.error('Admins only');
      } else {
        toast.error('Sign-in required to join');
      }
    };
    socket.on('roomCreateFailed', onCreateFailed);
    socket.on('roomForbidden', onForbidden);
    return () => {
      socket.off('join', onJoin);
      socket.off('userJoined', onUserJoined);
      socket.off('userLeft', onUserLeft);
      socket.off('roomAnchorSet', onRoomAnchorSet);
      socket.off('roomAnchorCleared', onRoomAnchorCleared);
      socket.off('modelChanged', onModelChanged);
      socket.off('currentModel', onCurrentModel);
      socket.off('sequenceUpdated', onSequenceUpdated);
      socket.off('roomClosed', onRoomClosed);
      socket.off('roomCreateFailed', onCreateFailed);
      socket.off('roomForbidden', onForbidden);
    };
  }, [socket]);

  const createRoom = useCallback(
    (roomName: string, sequenceFile: string, visibility: RoomAccess['visibility'] = 'public') => {
      if (!socket) return;
      setState((s) => ({ ...s, loading: true }));
      // Server now mints the UID + enforces uniqueness in one shot, so
      // we pass visibility along with the create instead of PATCHing it
      // after the fact. The `join` event that follows carries the UID.
      socket.emit('createRoom', roomName, sequenceFile, visibility);
    },
    [socket],
  );

  const joinRoom = useCallback(
    (roomId: string) => {
      if (!socket) return;
      setState((s) => ({ ...s, loading: true }));
      socket.emit('joinRoom', roomId, null, 'control_ui');
    },
    [socket],
  );

  // Presenter-triggered reset for a mis-calibrated headset anchor (see
  // RoomControl's anchor status card) — headsets fall back to their own
  // default placement on their next recenter.
  const clearRoomAnchor = useCallback(() => {
    if (!socket || !state.room) return;
    socket.emit('clearRoomAnchor', state.room.id);
  }, [socket, state.room]);

  const next = useCallback(() => {
    if (!socket || !state.room) return;
    setState((s) => {
      if (!s.room?.sequence) return s;
      const n = s.room.sequence.assets.length;
      const nextIdx = (s.currentIndex + 1) % n;
      return { ...s, currentIndex: nextIdx };
    });
    socket.emit('nextModel', state.room.id);
  }, [socket, state.room, state.currentIndex]);

  const prev = useCallback(() => {
    if (!socket || !state.room) return;
    setState((s) => {
      if (!s.room?.sequence) return s;
      const n = s.room.sequence.assets.length;
      const prevIdx = (s.currentIndex - 1 + n) % n;
      return { ...s, currentIndex: prevIdx };
    });
    socket.emit('prevModel', state.room.id);
  }, [socket, state.room, state.currentIndex]);

  const jumpTo = useCallback(
    (index: number) => {
      if (!socket || !state.room?.sequence) return;
      const asset = state.room.sequence.assets[index];
      if (!asset) return;
      setState((s) => ({ ...s, currentIndex: index }));
      socket.emit('selectModel', state.room!.id, asset.model);
    },
    [socket, state.room],
  );

  // "Leave" for the operator: detach socket from the room but keep the room
  // alive server-side so AR clients aren't kicked out.
  const close = useCallback(() => {
    if (!socket || !state.room) return;
    socket.emit('leaveRoom', state.room.id);
    setState({ room: null, currentIndex: 0, loading: false });
  }, [socket, state.room]);

  return { ...state, createRoom, joinRoom, next, prev, jumpTo, close, clearRoomAnchor };
}
